import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Medal, Award, TrendingUp } from "lucide-react";
import { Database } from "@/integrations/supabase/types";

type ReferralPartner = Database["public"]["Tables"]["referral_partners"]["Row"];

interface LeaderboardEntry extends ReferralPartner {
  rank: number;
}

export default function ReferralLeaderboard() {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const { data, error } = await supabase
        .from("referral_partners")
        .select("*")
        .eq("status", "active")
        .order("total_commission_earned", { ascending: false })
        .limit(10);

      if (error) throw error;

      const rankedData = (data || []).map((partner, index) => ({
        ...partner,
        rank: index + 1,
      }));

      setLeaderboard(rankedData);
    } catch (error) {
      console.error("Error loading leaderboard:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-6 w-6 text-yellow-500" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-400" />;
      case 3:
        return <Award className="h-6 w-6 text-amber-600" />;
      default:
        return <TrendingUp className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <p className="text-center text-muted-foreground">Loading leaderboard...</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Referral Accelerator Leaderboard</h2>
        <Badge variant="default">90-Day Program</Badge>
      </div>

      <div className="space-y-4">
        {leaderboard.length === 0 ? (
          <p className="text-center text-muted-foreground">No active partners yet</p>
        ) : (
          leaderboard.map((entry) => (
            <div
              key={entry.id}
              className={`flex items-center justify-between p-4 rounded-lg border ${
                entry.rank <= 3 ? "bg-muted/50" : ""
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-10">
                  {getRankIcon(entry.rank)}
                </div>
                <div>
                  <p className="font-semibold">
                    {entry.first_name} {entry.last_name}
                  </p>
                  {entry.company_name && (
                    <p className="text-sm text-muted-foreground">{entry.company_name}</p>
                  )}
                </div>
              </div>

              <div className="text-right">
                <p className="font-bold">{formatCurrency(entry.total_commission_earned)}</p>
                <p className="text-sm text-muted-foreground">
                  {entry.total_referrals} referrals
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-6 p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2">Prize Structure</h3>
        <ul className="space-y-1 text-sm">
          <li>🥇 1st Place: $5,000 bonus + Featured Partner badge</li>
          <li>🥈 2nd Place: $2,000 bonus + Early feature access</li>
          <li>🥉 3rd Place: $1,000 bonus + Co-branded case study</li>
          <li>🎯 3+ Closed Deals: Backstage roadmap access</li>
        </ul>
      </div>
    </Card>
  );
}
